// Loading overlay and error display

import { t } from '@/i18n'
import { debug } from '@/utils/debug'
import { loadingOverlay, loadingStatus } from './elements'
import { updateProgress } from './progress'

/**
 * Show loading overlay with message
 */
export function showLoading(message: string = 'Loading...'): void {
  loadingStatus.textContent = message
  loadingOverlay.classList.remove('hidden')
  // Update progress based on the message
  updateProgress(message)
}

/**
 * Hide loading overlay
 */
export function hideLoading(): void {
  loadingOverlay.classList.add('hidden')
  loadingOverlay.classList.remove('error')
}

/**
 * Get the appropriate error message key based on error type
 */
export function getErrorMessageKey(error: unknown): string {
  // Network errors (fetch failed)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return 'error.network'
  }

  // Check for HTTP status in error
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    if (message.includes('500') || message.includes('server error')) {
      return 'error.serverError'
    }
    if (message.includes('403') || message.includes('forbidden') || message.includes('access')) {
      return 'error.noAccess'
    }
    if (message.includes('404') || message.includes('not found')) {
      return 'error.notFound'
    }
  }

  // Check for status property on error object
  const errorWithStatus = error as { status?: number }
  if (errorWithStatus.status) {
    switch (errorWithStatus.status) {
      case 403: return 'error.noAccess'
      case 404: return 'error.notFound'
      case 500: return 'error.serverError'
    }
  }

  // Default to generic load failed
  return 'error.loadFailed'
}

/**
 * Show error in the loading overlay with error styling
 */
export function showError(error: unknown): void {
  const messageKey = getErrorMessageKey(error)
  loadingStatus.textContent = t(messageKey)
  loadingOverlay.classList.remove('hidden')
  loadingOverlay.classList.add('error')
  debug.error('App', `Showing error: ${messageKey}`, error)
}
